'use client';

import React, { useEffect } from 'react';

import { css, cx } from '@pt-group-fe/styled-system/css';
import {
  footer,
  languageNavigation,
  mainNavigationItem,
  mobileNavigation
} from '@pt-group-fe/styled-system/recipes';
import { usePathname, useRouter } from 'next/navigation';

import { LANGUAGES_TRANS } from '@lib/config';
import { useLogger } from '@lib/hooks';
import { Cms_SitemapAlternateLang } from '@lib/services/cms';
import { Locale } from '@lib/services/i18n';
import { Icon } from '@lib/ui/icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@lib/ui/select';

interface LanguageNavigationProps {
  position: 'footer' | 'header' | 'mobile';
  languages: Locale[];
  current: Locale;
  fallback: Locale;
  alternateLangs?: Cms_SitemapAlternateLang[];
}

const LanguageNavigation: React.FC<LanguageNavigationProps> = ({
  languages,
  fallback,
  current,
  alternateLangs,
  position
}) => {
  const log = useLogger('LanguageNavigation');
  const { push } = useRouter();
  const [isOpen, setOpen] = React.useState(false);
  const pathname = usePathname();

  const {
    selectContent,
    selectContentMobile,
    headerContainer,
    headerLangSelect,
    trigger,
    selectTrigger,
    selectTriggerIcon,
    selectItemMobile,
    selectItem,
    mobilenNavContainer
  } = languageNavigation({
    isOpen
  });

  const handleSelect = (lng: Locale) => {
    if (lng === current) return;

    const alt = alternateLangs?.find(lang => lang.locale === lng);

    if (!alt || !alt.path) {
      log.debug('No alternate language content found for', lng);
      push(`/${lng}`);
    }

    if (!alt) return;

    push(alt.path);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false);
  }, [pathname]);

  if (languages.length === 0) {
    return null;
  }

  if (position === 'mobile') {
    if (languages.length < 2) {
      return null;
    }

    return (
      <div className={mobilenNavContainer}>
        <Select value={current} onValueChange={handleSelect} open={isOpen} onOpenChange={setOpen}>
          <SelectTrigger
            noIcon
            className={cx('group', mainNavigationItem().itemTrigger, selectTrigger)}
          >
            <SelectValue />
            <Icon
              i="angle-down-circle"
              size="small"
              className={cx(mainNavigationItem().navIcon, selectTriggerIcon)}
            />
          </SelectTrigger>
          <SelectContent className={cx(selectContentMobile, css({ zIndex: 4000 }))} align="start">
            {languages.map(lang => (
              <SelectItem
                key={lang}
                value={lang}
                className={cx(mobileNavigation().navItemLabel, selectItemMobile)}
              >
                {LANGUAGES_TRANS[lang] || lang}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (position === 'header') {
    if (languages.length < 2) {
      return null;
    }

    return (
      <div className={cx(headerContainer, css({ pl: 5 }))}>
        <Select value={current} onValueChange={handleSelect} open={isOpen} onOpenChange={setOpen}>
          <SelectTrigger
            noIcon
            className={cx('group', mainNavigationItem().itemTrigger, headerLangSelect)}
          >
            {current?.toUpperCase()}
            {/* {LANGUAGES_TRANS[current]} */}
            <Icon i="angle-down-circle" size="small" className={mainNavigationItem().navIcon} />
          </SelectTrigger>
          <SelectContent
            className={cx(selectContent, css({ zIndex: 2000 }))}
            align="end"
            sideOffset={10}
          >
            {languages.map(lang => (
              <SelectItem key={lang} value={lang} className={selectItem}>
                {LANGUAGES_TRANS[lang] || lang}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (languages.length === 1) {
    const key = languages[0] || fallback;

    return LANGUAGES_TRANS[key] || null;
  }

  return (
    <Select value={current} open={isOpen} onOpenChange={setOpen} onValueChange={handleSelect}>
      <SelectTrigger
        className={cx(
          footer({ isLang: true }).bottomNavItem,
          trigger,
          css({ border: 'none', p: 0, outline: 'none', bg: 'transparent' })
        )}
        noIcon
      >
        {LANGUAGES_TRANS[current]}
      </SelectTrigger>
      <SelectContent className={selectContent}>
        {languages.map(lang => (
          <SelectItem key={lang} value={lang} className={selectItem}>
            {LANGUAGES_TRANS[lang] || lang}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export { LanguageNavigation };
export type { LanguageNavigationProps };
